import axios from 'axios';
import { getCentralLoginRedirect } from '../config';
import { AssignmentProgressResponse } from '../types';
import { canRedirectToAuth, markRedirectToAuth } from '../utils/authRedirectGuard';

// Production (test.pikinlab.ru): относительный /api/v1. Dev: proxy на 8001.
const baseURL =
  (process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ''
    ? process.env.REACT_APP_API_URL
    : '/api/v1');

const api = axios.create({
  baseURL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  // 5 мин для долгих операций (AI-генерация, экспорт HTML) — иначе возможен 504
  timeout: 5 * 60 * 1000,
});

/**
 * Возвращает полный URL для статического изображения (вопрос/ответ).
 * В dev с proxy использует текущий origin, чтобы запрос шёл через proxy на backend.
 */
export function getStaticImageUrl(imageUrl: string | null | undefined): string {
  if (!imageUrl || typeof imageUrl !== 'string') return '';
  const trimmed = imageUrl.trim();
  if (!trimmed) return '';

  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    try {
      const externalUrl = new URL(trimmed);
      const catalogPathPrefix = '/product_photos/1600/photos/';
      if (
        externalUrl.hostname.toLocaleLowerCase('en') === 'catalog.febest.club'
        && externalUrl.pathname.startsWith(catalogPathPrefix)
      ) {
        const encodedFilename = externalUrl.pathname.slice(catalogPathPrefix.length);
        const filename = decodeURIComponent(encodedFilename);
        if (filename && !filename.includes('/') && !filename.includes('\\')) {
          return `${window.location.origin}/api/v1/images/catalog/${encodeURIComponent(filename)}`;
        }
      }
    } catch {
      // Оставляем исходный URL, если он не разбирается стандартным URL API.
    }
    return trimmed;
  }

  const apiBase = (process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ''
    ? process.env.REACT_APP_API_URL
    : '/api/v1');
  const staticBase = apiBase.startsWith('http') ? apiBase.replace(/\/api\/v1\/?$/, '') : window.location.origin;
  const path = trimmed.startsWith('/') ? trimmed : `/static/images/${trimmed}`;
  return `${staticBase}${path}`;
}

export function getAssignmentProgress(testId: number) {
  return api.get<AssignmentProgressResponse>(`/admin/tests/${testId}/assignment-progress`);
}

// Interceptor для обработки ошибок (401 → редирект на auth)
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status !== 401) return Promise.reject(error);
    const url = String(error.config?.url ?? '');
    if (url.includes('sso-session')) {
      return Promise.reject(error);
    }
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete api.defaults.headers.common['Authorization'];
    if (!canRedirectToAuth()) return Promise.reject(error);
    markRedirectToAuth();
    window.location.replace(getCentralLoginRedirect());
    return Promise.reject(error);
  }
);

// Interceptor для добавления токена к запросам
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export default api;
